import React, { useEffect, useState, useRef } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { ImagePlus, X, Send, Globe, Eye, EyeOff, Trash2, Edit3, Image as ImageIcon, Users } from "lucide-react";

const ALL_CLASSES = ["Toddlers", "Early Years 1", "Early Years 2", "Early Years 3"];

function AuthImage({ path, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let alive = true;
    adminApi.get(path, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className={`${className} bg-[#F5F5F0] animate-pulse`} />;
  return <img src={src} alt="" className={className} loading="lazy" decoding="async" />;
}

export default function UpdatesPanel({ can }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/updates");
      setUpdates(res.data || []);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const togglePublish = async (u) => {
    try {
      await adminApi.patch(`/admin/updates/${u.id}`, { published: !u.published });
      toast.success(u.published ? "Unpublished" : "Published");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete "${u.title}"?`)) return;
    try {
      await adminApi.delete(`/admin/updates/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="updates-panel">
      <div className="p-5 md:p-7 border-b border-[#0F1B47]/8 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#0F1B47] font-medium">Weekly updates</h2>
          <p className="text-sm text-[#4A5568] mt-1">Share photos and notes from the week with parents.</p>
        </div>
        {can("manage_updates") && (
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b]"
            data-testid="updates-add-btn"
          >
            <Send size={14} /> New update
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#718096] text-sm">Loading…</div>
      ) : updates.length === 0 ? (
        <div className="p-12 text-center">
          <div className="text-[#0F1B47] font-medium mb-1">No updates yet</div>
          <p className="text-sm text-[#718096]">{can("manage_updates") ? "Click \"New update\" to share something with parents." : "No updates have been posted."}</p>
        </div>
      ) : (
        <ul data-testid="updates-list">
          {updates.map((u) => (
            <li key={u.id} className="border-t border-[#0F1B47]/8 p-5 md:p-6" data-testid={`update-row-${u.id}`}>
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    {!u.published && (
                      <span className="text-[10px] font-bold tracking-[0.16em] uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Draft</span>
                    )}
                    {u.audience === "classes" && Array.isArray(u.classes) && u.classes.length > 0 ? (
                      u.classes.map((c) => (
                        <span key={c} className="text-[10px] font-bold tracking-[0.12em] uppercase px-2 py-0.5 rounded-full bg-[#E3EDF9] text-[#0F1B47]">{c}</span>
                      ))
                    ) : (
                      <span className="text-[10px] font-bold tracking-[0.12em] uppercase px-2 py-0.5 rounded-full bg-[#0F1B47]/8 text-[#0F1B47]">All parents</span>
                    )}
                    {u.week_label && <span className="text-[11px] text-[#718096]">{u.week_label}</span>}
                  </div>
                  <div className="font-display text-lg text-[#0F1B47] font-medium">{u.title}</div>
                  {u.body && <p className="text-sm text-[#4A5568] mt-1.5 line-clamp-3 whitespace-pre-wrap">{u.body}</p>}
                  {u.photos?.length > 0 && (
                    <div className="mt-3 flex gap-2 overflow-x-auto">
                      {u.photos.map((p) => (
                        <AuthImage
                          key={p.id}
                          path={`/admin/updates/${u.id}/photos/${p.id}`}
                          className="w-20 h-20 rounded-xl object-cover flex-shrink-0 bg-[#F5F5F0]"
                        />
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] text-[#718096] mt-3">
                    Posted {new Date(u.created_at).toLocaleString()} by {u.created_by}
                  </div>
                </div>
                {can("manage_updates") && (
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <button
                      onClick={() => togglePublish(u)}
                      className="text-xs font-semibold text-[#0F1B47] px-3 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10 inline-flex items-center gap-1"
                      data-testid={`update-toggle-${u.id}`}
                    >
                      {u.published ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                    </button>
                    <button
                      onClick={() => { setEditing(u); setShowForm(true); }}
                      className="text-xs font-semibold text-[#0F1B47] px-3 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10 inline-flex items-center gap-1"
                      data-testid={`update-edit-${u.id}`}
                    >
                      <Edit3 size={12} /> Edit
                    </button>
                    <button
                      onClick={() => remove(u)}
                      className="text-xs font-semibold text-red-700 px-3 py-2 rounded-full bg-red-50 hover:bg-red-100 inline-flex items-center gap-1"
                      data-testid={`update-delete-${u.id}`}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <UpdateForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function UpdateForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [title, setTitle] = useState(initial?.title || "");
  const [body, setBody] = useState(initial?.body || "");
  const [weekLabel, setWeekLabel] = useState(initial?.week_label || `Week of ${new Date().toLocaleDateString(undefined, { day: "numeric", month: "short" })}`);
  const [audience, setAudience] = useState(initial?.audience === "classes" ? "classes" : "all");
  const [selectedClasses, setSelectedClasses] = useState(
    Array.isArray(initial?.classes) ? initial.classes.filter((c) => ALL_CLASSES.includes(c)) : []
  );
  const [published, setPublished] = useState(initial?.published ?? true);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const fileInput = useRef(null);

  const toggleClass = (c) => {
    setSelectedClasses((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  // iPhone photos are HEIC by default, which <img>/canvas cannot decode on most
  // browsers — so they'd skip compression and arrive at full size. Convert HEIC to
  // JPEG first (lazy-loaded so the heavy decoder only loads when actually needed).
  const toDecodable = async (file) => {
    const isHeic = /image\/hei[cf]/i.test(file.type || "") || /\.(heic|heif)$/i.test(file.name || "");
    if (!isHeic) return file;
    try {
      const { default: heic2any } = await import("heic2any");
      const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
      const blob = Array.isArray(out) ? out[0] : out;
      return new File([blob], (file.name || "photo").replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
    } catch {
      return file; // fall through; canvas will try, and the size guard will catch it
    }
  };

  // Downscale + JPEG-encode an image so the multipart POST stays well under the ingress limit.
  // 1600px max on the long edge, JPEG q=0.82 — typical phone photo (4-6 MB) -> ~250-500 KB.
  const compressImage = async (input) => {
    const file = await toDecodable(input);
    return new Promise((resolve) => {
      if (!file.type?.startsWith("image/")) return resolve(file);
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const MAX = 1600;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const scale = Math.min(MAX / width, MAX / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return resolve(file);
          const name = (file.name || "photo").replace(/\.(heic|heif|webp|png|gif)$/i, ".jpg");
          resolve(new File([blob], name, { type: "image/jpeg" }));
        }, "image/jpeg", 0.82);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  };

  const onPickFiles = async (e) => {
    const picked = Array.from(e.target.files || []).slice(0, 12);
    if (picked.length === 0) return;
    setProcessing(true);
    try {
      const compressed = await Promise.all(picked.map(compressImage));
      const MAX_BYTES = 8 * 1024 * 1024; // keep in sync with backend MAX_UPDATE_PHOTO_BYTES
      const tooBig = compressed.filter((f) => f.size > MAX_BYTES);
      if (tooBig.length > 0) {
        toast.error(`${tooBig.length} photo${tooBig.length === 1 ? "" : "s"} couldn't be compressed enough. Try re-saving ${tooBig.length === 1 ? "it" : "them"} as JPEG, or upload a smaller copy.`);
      }
      const accepted = compressed.filter((f) => f.size <= MAX_BYTES);
      setFiles(accepted);
      // Revoke any previous preview URLs to avoid leaks.
      setPreviews((prev) => { prev.forEach((u) => URL.revokeObjectURL(u)); return accepted.map((f) => URL.createObjectURL(f)); });
    } finally {
      setProcessing(false);
    }
  };

  const totalKB = files.reduce((a, f) => a + f.size, 0) / 1024;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title is required");
    if (audience === "classes" && selectedClasses.length === 0) {
      return toast.error("Select at least one class");
    }
    if (processing) return toast.info("Still preparing photos…");
    setSaving(true);
    try {
      const classesPayload = audience === "classes" ? selectedClasses : [];
      if (isEdit) {
        await adminApi.patch(`/admin/updates/${initial.id}`, {
          title: title.trim(),
          body: body.trim(),
          week_label: weekLabel.trim(),
          audience,
          classes: classesPayload,
          published,
        });
        toast.success("Update saved");
      } else {
        const fd = new FormData();
        fd.append("title", title.trim());
        fd.append("body", body.trim());
        fd.append("week_label", weekLabel.trim());
        fd.append("audience", audience);
        fd.append("classes_json", JSON.stringify(classesPayload));
        fd.append("published", String(published));
        files.forEach((f) => fd.append("photos", f));
        await adminApi.post("/admin/updates", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        toast.success("Update posted");
      }
      onSaved();
    } catch (err) {
      // Network errors (request never reached backend, e.g. ingress upload limit) have no err.response.
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (detail) {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      } else if (!err?.response) {
        toast.error(
          files.length > 0
            ? "Network error while uploading photos. Try fewer photos or check your connection."
            : "Network error. Please check your connection and try again."
        );
      } else {
        toast.error(`Save failed (HTTP ${status || "?"})`);
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/40 flex items-center justify-center p-3 md:p-6" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#FDFDF9] rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" data-testid="update-form">
        <div className="sticky top-0 bg-[#FDFDF9] border-b border-[#0F1B47]/8 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display text-xl text-[#0F1B47] font-medium">{isEdit ? "Edit update" : "New weekly update"}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5">
          <FormField label="Send to">
            <div className="flex gap-2" role="radiogroup" data-testid="update-form-audience">
              <button
                type="button"
                role="radio"
                aria-checked={audience === "all"}
                onClick={() => setAudience("all")}
                className={`flex-1 inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold border transition ${
                  audience === "all"
                    ? "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]"
                    : "bg-[#F5F5F0] text-[#0F1B47] border-[#0F1B47]/12 hover:border-[#0F1B47]/30"
                }`}
                data-testid="update-form-audience-all"
              >
                <Globe size={14} /> All parents
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={audience === "classes"}
                onClick={() => setAudience("classes")}
                className={`flex-1 inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold border transition ${
                  audience === "classes"
                    ? "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]"
                    : "bg-[#F5F5F0] text-[#0F1B47] border-[#0F1B47]/12 hover:border-[#0F1B47]/30"
                }`}
                data-testid="update-form-audience-classes"
              >
                <Users size={14} /> Specific classes
              </button>
            </div>
            {audience === "classes" ? (
              <div className="mt-3 grid grid-cols-2 gap-2" data-testid="update-form-classes">
                {ALL_CLASSES.map((c) => {
                  const active = selectedClasses.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleClass(c)}
                      className={`text-left rounded-2xl px-4 py-2.5 text-sm font-medium border transition ${
                        active
                          ? "bg-[#E3EDF9] border-[#648DE5] text-[#0F1B47]"
                          : "bg-[#FDFDF9] border-[#0F1B47]/12 text-[#4A5568] hover:border-[#0F1B47]/30"
                      }`}
                      data-testid={`update-form-class-${c.replace(/\s+/g, "-").toLowerCase()}`}
                      aria-pressed={active}
                    >
                      <span className={`inline-block w-3.5 h-3.5 mr-2 rounded-[5px] border align-middle ${active ? "bg-[#0F1B47] border-[#0F1B47]" : "bg-[#FDFDF9] border-[#0F1B47]/30"}`} />
                      {c}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="mt-2 text-xs text-[#718096]">Every signed-in parent will see this update.</p>
            )}
          </FormField>

          <FormField label="Title *">
            <input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="e.g. Sand play & storytelling" className={inputCls} data-testid="update-form-title" />
          </FormField>

          <FormField label="Week label">
            <input value={weekLabel} onChange={(e) => setWeekLabel(e.target.value)} placeholder="e.g. Week of 5 May 2026" className={inputCls} data-testid="update-form-week" />
          </FormField>

          <FormField label="Write-up">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Tell parents what their children explored this week…" className={inputCls} data-testid="update-form-body" />
          </FormField>

          {!isEdit && (
            <FormField label="Photos (optional, up to 12)">
              <input ref={fileInput} type="file" multiple accept="image/*" onChange={onPickFiles} className="hidden" />
              <button type="button" onClick={() => fileInput.current?.click()} disabled={processing} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10 disabled:opacity-60" data-testid="update-form-photos">
                <ImagePlus size={14} />
                {processing
                  ? "Preparing photos…"
                  : files.length > 0
                    ? `${files.length} photo${files.length === 1 ? "" : "s"} ready · ${totalKB < 1024 ? `${Math.round(totalKB)} KB` : `${(totalKB / 1024).toFixed(1)} MB`}`
                    : "Add photos"}
              </button>
              {files.length > 0 && !processing && (
                <p className="text-[11px] text-[#718096] mt-1.5">Photos are automatically resized to 1600 px and compressed before upload — original file size doesn&apos;t matter.</p>
              )}
              {previews.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {previews.map((p, i) => (
                    <img key={i} src={p} alt="" className="w-full aspect-square rounded-xl object-cover" />
                  ))}
                </div>
              )}
            </FormField>
          )}

          {isEdit && initial?.photos?.length > 0 && (
            <div className="text-[12px] text-[#718096] flex items-center gap-2">
              <ImageIcon size={14} /> {initial.photos.length} photo{initial.photos.length === 1 ? "" : "s"} attached. To replace photos, delete this update and create a new one.
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="w-4 h-4 accent-[#0F1B47]" data-testid="update-form-published" />
            <span className="text-[#0F1B47]">Publish immediately {!published && <span className="text-[#718096]">(save as draft)</span>}</span>
          </label>
        </div>

        <div className="sticky bottom-0 bg-[#FDFDF9] border-t border-[#0F1B47]/8 px-6 py-4 flex items-center gap-3">
          <button type="submit" disabled={saving || processing} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="update-form-save">
            {saving ? "Saving…" : processing ? "Preparing photos…" : (isEdit ? "Save changes" : (published ? "Publish update" : "Save draft"))}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]";

function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
